import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import ShoppingCart from '../components/ShoppingCart';
import '../styles/CheckoutPage.css';
import Head from "../components/Head";
import auth from '../utils/auth';
import localCart from '../utils/localCartStorage';
import StripeContainer from '../components/StripeContainer';
import { scrollToTop } from '../hooks/useScrollToTop';
import { CartContext } from '../contexts/CartContext';

const CheckoutPage = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        email: '',
        careOf: '',
        address: '',
        region: '',
        postalCode: '',
        country: 'Sverige'
    });
    const [error, setError] = useState(null);
    // eslint-disable-next-line
    const [userName, setUserName] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [cartIsEmpty, setCartIsEmpty] = useState(false);
    const [showPaymentForm, setShowPaymentForm] = useState(false);
    // eslint-disable-next-line
    const [shippingValidated, setShippingValidated] = useState(false);
    const [checkoutType, setCheckoutType] = useState(null);
    const { fetchCartCount } = useContext(CartContext);
    const isAuthenticated = auth.isAuthenticated();

    useEffect(() => {
        const fetchUserData = async () => {
            if (isAuthenticated) {
                try {
                    const userId = auth.getUserId();
                    const role = auth.getUserRole();
                    const response = await fetch(
                        `${process.env.REACT_APP_API_BASE_URL}/users/${role}/${userId}`,
                        {
                            method: "GET",
                            headers: {
                                Authorization: `Bearer ${auth.getToken()}`
                            }
                        }
                    );

                    if (response.ok) {
                        const userData = await response.json();
                        setUserName(userData.profile.name || '');
                        setFormData({
                            email: userData.email || '',
                            careOf: userData.profile.careOf || '',
                            address: userData.profile.address || '',
                            region: userData.profile.region || '',
                            postalCode: userData.profile.postalCode || '',
                            country: userData.profile.country || 'Sverige'
                        });
                    }
                } catch (error) {
                    console.error("Failed to fetch user data:", error);
                }
            }
        };

        const checkCartStatus = async () => {
            try {
                let cartEmpty = false;

                if (isAuthenticated) {
                    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/cart`, {
                        headers: {
                            'Authorization': `Bearer ${auth.getToken()}`
                        }
                    });
                    if (response.ok) {
                        const data = await response.json();
                        cartEmpty = data.items.length === 0;
                    }
                } else {
                    const cart = localCart.getCart();
                    cartEmpty = cart.items.length === 0;
                }

                setCartIsEmpty(cartEmpty);
            } catch (error) {
                console.error('Error checking cart status:', error);
                setCartIsEmpty(true);
            } finally {
                setIsLoading(false);
            }
        };

        fetchUserData();
        checkCartStatus();
    }, [isAuthenticated]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const validateEmail = (email) => {
        if (!email || email.trim() === '') {
            return "E-postadress är obligatorisk.";
        }
        const emailRegex = /^[\w.-]+@([\w-]+\.)+[\w-]{2,4}$/gi;
        if (!emailRegex.test(email)) {
            return "Vänligen ange en giltig e-postadress.";
        }
        return null;
    };

    const validateCareOf = (careOf) => {
        if (careOf && careOf.trim() !== '') {
            const nameRegex = /^[^0-9_!¡?÷?¿/\\+=@#£$€%ˆ&*(){}|~<>;:[\]]{2,50}$/;
            if (!nameRegex.test(careOf)) {
                return "C/O måste vara mellan 2 och 50 tecken långt och får inte innehålla siffror eller specialtecken.";
            }
        }
        return null;
    };

    const validateAddress = (address) => {
        if (!address || address.trim() === '') {
            return "Adress är obligatorisk.";
        }
        const nameRegex = /^[^_!¡?÷?¿/\\+=@#£$€%ˆ&*(){}|~<>;:[\]]{2,50}$/;
        if (!nameRegex.test(address)) {
            return "Adressen måste vara mellan 2 och 50 tecken långt och får inte innehålla specialtecken.";
        }
        return null;
    };

    const validateRegion = (region) => {
        if (!region || region.trim() === '') {
            return "Ort/stad är obligatorisk.";
        }
        const nameRegex = /^[^0-9_!¡?÷?¿/\\+=@#£$€%ˆ&*(){}|~<>;:[\]]{2,50}$/;
        if (!nameRegex.test(region)) {
            return "Ort/staden måste vara mellan 2 och 50 tecken långt och får inte innehålla siffror eller specialtecken.";
        }
        return null;
    };

    const validatePostalCode = (postalCode) => {
        if (!postalCode || postalCode.trim() === '') {
            return "Postkod är obligatorisk.";
        }
        const postalCodeRegex = /^(\d{3}\s?\d{2})$/;
        if (!postalCodeRegex.test(postalCode)) {
            return "Postkoden måste bestå av 5 siffror och kan ha ett mellanslag efter de första 3 siffrorna.";
        }
        return null;
    };

    const validateForm = () => {
        const addressError = validateAddress(formData.address);
        if (addressError) return addressError;

        const regionError = validateRegion(formData.region);
        if (regionError) return regionError;

        const postalCodeError = validatePostalCode(formData.postalCode);
        if (postalCodeError) return postalCodeError;

        if (!isAuthenticated && checkoutType === 'guest') {
            const emailError = validateEmail(formData.email);
            if (emailError) return emailError;
        }

        const careOfError = validateCareOf(formData.careOf);
        if (careOfError) return careOfError;

        return null;
    };

    const handleValidateShipping = (e) => {
        e.preventDefault();
        setError(null);

        if (cartIsEmpty) {
            setError("Din kundvagn är tom. Lägg till produkter innan du fortsätter.");
            return;
        }

        const validationError = validateForm();
        if (validationError) {
            setError(validationError);
            return;
        }

        setShippingValidated(true);
        setShowPaymentForm(true);
    };

    const handlePaymentSuccess = async (paymentIntentId, orderId) => {
        setIsLoading(true);
        try {
            if (isAuthenticated) {
                const orderResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/orders`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${auth.getToken()}`
                    },
                    body: JSON.stringify({
                        shippingAddress: {
                            careOf: formData.careOf,
                            street: formData.address,
                            region: formData.region,
                            postalCode: formData.postalCode,
                            country: formData.country
                        },
                        paymentIntentId: paymentIntentId,
                        orderId: orderId
                    })
                });

                if (!orderResponse.ok) {
                    const errorData = await orderResponse.json();
                    throw new Error(errorData.message || 'Failed to create order');
                }

                const orderData = await orderResponse.json();
                navigate(`/order/${orderData.order.orderId}`);
            } else {
                const cart = localCart.getCart();

                const orderResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/orders/guest`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        email: formData.email,
                        shippingAddress: {
                            careOf: formData.careOf,
                            street: formData.address,
                            region: formData.region,
                            postalCode: formData.postalCode,
                            country: formData.country
                        },
                        items: cart.items,
                        paymentIntentId: paymentIntentId,
                        orderId: orderId
                    })
                });

                if (!orderResponse.ok) {
                    const errorData = await orderResponse.json();
                    throw new Error(errorData.message || 'Failed to create order');
                }

                const orderData = await orderResponse.json();

                localCart.clearCart();
                fetchCartCount();

                navigate(`/order/${orderData.order.orderId}`, { 
                    state: { 
                        guestEmail: formData.email,
                        isNewOrder: true
                    } 
                });
            }

            scrollToTop();
        } catch (error) {
            setError(error.message || 'Ett fel uppstod vid bearbetning av din beställning. Vänligen försök igen.');
        } finally {
            setIsLoading(false);
        }
    };

    const navigateToProducts = (event) => {
        event.preventDefault();
        navigate('/');
        scrollToTop();
    };

    const goBackToShipping = () => {
        setShowPaymentForm(false);
        setShippingValidated(false);
    };

    const goBackToCheckoutTypeSelection = () => {
        setCheckoutType(null);
    };

    const navigateToLogin = () => {
        navigate('/logga-in');
    };

    const handleCheckoutTypeSelection = (type) => {
        setCheckoutType(type);
    };

    if (isLoading) {
        return (
            <div className="loading-container">
                <div className="loading-box">
                    <div className="loading-text">Laddar kundvagn...</div>
                </div>
            </div>
        );
    }

    return (
        <div className="checkout-page">
            <Head title="EssayWriter | Kundvagn" />
            <h1>Kassa</h1>
            {cartIsEmpty ? (
                <div className="empty-cart-container">
                    <div className="empty-cart-message">
                        <h2>Din kundvagn är tom</h2>
                        <p>Du behöver lägga till produkter i din kundvagn innan du kan genomföra ett köp.</p>
                        <button className="continue-shopping-btn" onClick={navigateToProducts}>
                            Fortsätt handla
                        </button>
                    </div>
                </div>
            ) : (
                <div className="checkout-container">
                    <div className="checkout-form">
                        {!isAuthenticated && checkoutType === null ? (
                            <div className="checkout-type-selection">
                                <h2>Välj hur du vill fortsätta</h2>
                                <div className="checkout-options">
                                    <button
                                        className="checkout-option-btn"
                                        onClick={navigateToLogin}
                                    >
                                        Logga in
                                    </button>
                                    <p className="or-divider">eller</p>
                                    <button
                                        className="checkout-option-btn"
                                        onClick={() => handleCheckoutTypeSelection('guest')}
                                    >
                                        Fortsätt som gäst
                                    </button>
                                </div>
                            </div>
                        ) : !showPaymentForm ? (
                            <>
                                <h2>Leveransdetaljer</h2>
                                {error && <p className="error-message">{error}</p>}
                                <form onSubmit={handleValidateShipping}>
                                    {(!isAuthenticated && checkoutType === 'guest') && (
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            placeholder="E-postadress *"
                                            required
                                        />
                                    )}
                                    <input
                                        type="text"
                                        name="careOf"
                                        value={formData.careOf}
                                        onChange={handleInputChange}
                                        placeholder="C/O (valfritt)"
                                    />
                                    <input
                                        type="text"
                                        name="address"
                                        value={formData.address}
                                        onChange={handleInputChange}
                                        placeholder="Adress *"
                                        required
                                    />
                                    <input
                                        type="text"
                                        name="region"
                                        value={formData.region}
                                        onChange={handleInputChange}
                                        placeholder="Ort / stad *"
                                        required
                                    />
                                    <input
                                        type="text"
                                        name="postalCode"
                                        value={formData.postalCode}
                                        onChange={handleInputChange}
                                        placeholder="Postkod *"
                                        required
                                    />
                                    <select
                                        name="country"
                                        value={formData.country}
                                        onChange={handleInputChange}
                                        className="checkout-input"
                                        required
                                    >
                                        <option value="Sverige">Sverige</option>
                                    </select>

                                    <div className="checkout-form-buttons">
                                        {!isAuthenticated && checkoutType === 'guest' && (
                                            <button 
                                                type="button" 
                                                className="back-btn" 
                                                onClick={goBackToCheckoutTypeSelection}
                                            >
                                                Tillbaka
                                            </button>
                                        )}
                                        <button 
                                            className="checkout-form-btn" 
                                            type="submit" 
                                            disabled={isLoading || cartIsEmpty}
                                        >
                                            Fortsätt till betalning
                                        </button>
                                    </div>

                                    {!isAuthenticated && checkoutType === 'guest' && (
                                        <p className="guest-checkout-note">
                                            <strong>OBS!</strong> Din beställning kommer att kopplas till din e-postadress.
                                        </p>
                                    )}
                                </form>
                            </>
                        ) : (
                            <>
                                <h2>Betalningsdetaljer</h2>
                                {error && <p className="error-message">{error}</p>}
                                <div className="shipping-summary">
                                    <h3>Leveransdetaljer</h3>
                                    {!isAuthenticated && <p>E-post: {formData.email}</p>}
                                    <p>{formData.careOf ? `C/O ${formData.careOf}` : ''}</p>
                                    <p>{formData.address}</p>
                                    <p>{formData.postalCode} {formData.region}</p>
                                    <p>{formData.country}</p>
                                    <button
                                        type="button"
                                        className="edit-address-btn"
                                        onClick={goBackToShipping}
                                    >
                                        Redigera
                                    </button>
                                </div>
                                <div className="payment-container">
                                    <p className="payment-method">Kort/kreditkort</p>
                                    <StripeContainer
                                        onPaymentSuccess={handlePaymentSuccess}
                                        isLoading={isLoading}
                                        guestEmail={!isAuthenticated ? formData.email : null}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                    <div className="checkout-cart">
                        <ShoppingCart onCartEmptyChange={setCartIsEmpty} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default CheckoutPage;